// components/BirthdaySheet.js
//
// Asking someone when their birthday is, once, at a moment where it makes
// sense — and letting them say no.
//
// ── Why this is not on the sign-up form ────────────────────────────────────
//
// Making it mandatory at sign-up would put friction back into the one place
// we have just spent effort taking it out of, and for a worse reason: a date
// of birth demanded before anyone has seen what the product does reads as an
// ID check. People either bounce or type 01/01/1990.
//
// So it is asked AFTER their first invitation is published — when they have
// just done the thing the reminders are about, and the offer ("we will remind
// you to plan your own") lands as a feature rather than a form field.
//
// ── Why the year is optional ───────────────────────────────────────────────
//
// Nothing here needs it. A reminder a week before a birthday needs a month and
// a day; the year only says how old someone is. Asking for it costs completion
// and buys nothing, so it is last, clearly marked optional, and stored as a
// sentinel when skipped.

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { formatBirthday } from '../shared/birthdayReminders';
import './BirthdaySheet.scss';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const daysIn = (month, year) => {
  if (!month) return 31;
  // Year unknown means February has to allow the 29th — someone born on a
  // leap day should not be told their birthday does not exist.
  return new Date(year || 2024, month, 0).getDate();
};

export default function BirthdaySheet({ onSave, onDismiss }) {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onDismiss]);

  const maxDay = daysIn(Number(month), Number(year));

  const save = async () => {
    if (!month || !day) {
      setError('Pick a month and a day.');
      return;
    }
    if (Number(day) > maxDay) {
      setError(`${MONTHS[Number(month) - 1]} does not have ${day} days.`);
      return;
    }
    if (year && (Number(year) < 1900 || Number(year) > new Date().getFullYear())) {
      setError('That year does not look right.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(formatBirthday({
        year: year ? Number(year) : null,
        month: Number(month),
        day: Number(day),
      }));
    } catch (err) {
      setError(err.message || 'Could not save that.');
      setSaving(false);
    }
  };

  return (
    <div className="bday-scrim" onClick={onDismiss}>
      <div
        className="bday-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Your birthday"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bday-head">
          <h2>When is your birthday?</h2>
          <button type="button" onClick={onDismiss} aria-label="Not now"><FiX /></button>
        </div>

        <div className="bday-body">
          {/* The reason, first. A date of birth asked for without one reads
              as an ID check; with one it reads as an offer. */}
          <p className="bday-why">
            We will remind you a week before, so you have time to get everyone in
            one place. Nothing goes on your profile.
          </p>

          <div className="bday-row">
            <div className="bday-field">
              <label htmlFor="bday-month">Month</label>
              <select
                id="bday-month"
                value={month}
                onChange={(e) => { setMonth(e.target.value); setError(null); }}
              >
                <option value="">—</option>
                {MONTHS.map((name, index) => (
                  <option key={name} value={index + 1}>{name}</option>
                ))}
              </select>
            </div>

            <div className="bday-field">
              <label htmlFor="bday-day">Day</label>
              <select
                id="bday-day"
                value={day}
                onChange={(e) => { setDay(e.target.value); setError(null); }}
              >
                <option value="">—</option>
                {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            <div className="bday-field">
              <label htmlFor="bday-year">
                Year <span className="bday-optional">optional</span>
              </label>
              <input
                id="bday-year"
                type="number"
                inputMode="numeric"
                placeholder="—"
                min="1900"
                max={new Date().getFullYear()}
                value={year}
                onChange={(e) => { setYear(e.target.value); setError(null); }}
              />
            </div>
          </div>

          {error && <p className="bday-error">{error}</p>}
        </div>

        <div className="bday-actions">
          <button type="button" className="bday-btn" onClick={onDismiss}>
            Not now
          </button>
          <button
            type="button"
            className="bday-btn bday-btn--primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Remind me'}
          </button>
        </div>
      </div>
    </div>
  );
}
